// cSpell:ignore Alles betaalgegevens eigenaar Eigenaar huurder huurders keer voor zonder
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

// "Alles in één keer per eigenaar" (Lewis, 2026-10-19). One payment form pays
// every tenant it bills to the one beneficiary chosen in it, so tenants whose
// units have different owners never share a form: that would hand one owner
// the money of another owner's tenant (Lewis, 2026-10-05). Instead each owner
// (each set of owners of a unit) gets a form of its own for their tenants,
// and the beneficiary is chosen in each as in the one-owner form (there is no
// default beneficiary). A unit without a known owner stays out of them: its
// tenants are billed one by one.

// Below two tenants one form per owner saves nothing over one per tenant.
const MIN_TENANTS_PER_OWNER_RUN = 2;

export interface IOwnerPayGroup<Share> {
  // The unit's owners: their sorted account ids joined (see
  // usePropertyOwners), the same for every unit of the same owner(s).
  ownerKey: string;
  // Their names ("Els Eigenaar", "A & B"), when known.
  ownerName?: string;
  shares: Share[];
}

export interface IOwnerPayGroups<Share> {
  // In the order of the tenants: the owner of the first tenant goes first.
  groups: IOwnerPayGroup<Share>[];
  // The tenants of a unit whose owner is not known: billed one by one.
  withoutOwner: Share[];
}

// The tenants to bill, grouped by the owner(s) of their unit.
export const groupSharesByOwner = <Share>(params: {
  shares: Share[];
  propertyIdOf: (share: Share) => number;
  ownerKeyByProperty: Record<number, string>;
  nameByKey: Record<string, string>;
}): IOwnerPayGroups<Share> => {
  const { shares, propertyIdOf, ownerKeyByProperty, nameByKey } = params;
  const groups: IOwnerPayGroup<Share>[] = [];
  const withoutOwner: Share[] = [];
  shares.forEach(share => {
    const ownerKey = ownerKeyByProperty[propertyIdOf(share)] ?? "";
    if (!ownerKey) {
      withoutOwner.push(share);
      return;
    }
    const group = groups.find(candidate => candidate.ownerKey === ownerKey);
    if (group) {
      group.shares.push(share);
      return;
    }
    groups.push({ ownerKey, ownerName: nameByKey[ownerKey], shares: [share] });
  });
  return { groups, withoutOwner };
};

// Whether "Alles in één keer per eigenaar" is the form to offer: the tenants
// do not all have the one known owner the plain "Alles in één keer" needs,
// and at least one owner has two tenants to bill together.
export const canPayPerOwner = (grouped: IOwnerPayGroups<unknown>): boolean => {
  const { groups, withoutOwner } = grouped;
  const isOneOwner = groups.length === 1 && withoutOwner.length === 0;
  return (
    !isOneOwner &&
    groups.some(group => group.shares.length >= MIN_TENANTS_PER_OWNER_RUN)
  );
};

// One payment form of "Alles in één keer per eigenaar": an owner's tenants,
// or a single tenant of a unit without a known owner (no ownerKey).
export interface IOwnerPayRun<Share> {
  ownerKey?: string;
  ownerName?: string;
  shares: Share[];
}

// The forms one after the other: the owners first, then the tenants without
// an owner, one by one.
export const getOwnerPayRuns = <Share>(
  grouped: IOwnerPayGroups<Share>,
): IOwnerPayRun<Share>[] => [
  ...grouped.groups,
  ...grouped.withoutOwner.map(share => ({ shares: [share] })),
];

// Index of the first form at or after `from` that still has a tenant without
// a payment order, or -1 when every one of them has one. A form whose tenants
// all have an order is never opened again: that would bill them twice.
export const getNextOpenRunIndex = (params: {
  runs: { shares: { contractId: string }[] }[];
  orderByContract: Record<string, number>;
  from: number;
}): number => {
  const { runs, orderByContract, from } = params;
  for (let index = Math.max(0, from); index < runs.length; index += 1) {
    if (
      runs[index].shares.some(
        share => orderByContract[share.contractId] == null,
      )
    ) {
      return index;
    }
  }
  return -1;
};

// An owner's line in the list of forms: "Els Eigenaar · 4 huurders".
export const ownerGroupLabel = (group: {
  ownerName?: string;
  shares: unknown[];
}): string => {
  const owner = group.ownerName || getLocalizedText("system.owner");
  return group.shares.length === 1
    ? getLocalizedText("settlement.payments.owner_group_one", { owner })
    : getLocalizedText("settlement.payments.owner_group_many", {
        owner,
        count: String(group.shares.length),
      });
};

// The owners in one sentence, for the blue card above the choice (Lewis,
// 2026-10-21: the loose lines under the options were unclear): "Bij "Alles
// in één keer per eigenaar" vul je de betaalgegevens één keer in per
// eigenaar: Valerie Eigenaar (3 huurders), Mr. Monopoly (1 huurder)." and,
// when some tenants have no owner, how those go.
export const ownerGroupsSentence = (grouped: {
  groups: { ownerName?: string; shares: unknown[] }[];
  withoutOwner: unknown[];
}): string => {
  const owners = grouped.groups
    .map(group => {
      const owner = group.ownerName || getLocalizedText("system.owner");
      return group.shares.length === 1
        ? getLocalizedText("settlement.payments.owner_item_one", { owner })
        : getLocalizedText("settlement.payments.owner_item_many", {
            owner,
            count: String(group.shares.length),
          });
    })
    .join(", ");
  const count = grouped.withoutOwner.length;
  let withoutOwner: string | undefined;
  if (count === 1) {
    withoutOwner = getLocalizedText(
      "costs.modal.payment_orders_without_owner_one",
    );
  } else if (count > 1) {
    withoutOwner = getLocalizedText(
      "costs.modal.payment_orders_without_owner_many",
      { count: String(count) },
    );
  }
  return [
    getLocalizedText("costs.modal.payment_orders_per_owner", { owners }),
    withoutOwner,
  ]
    .filter(Boolean)
    .join(" ");
};

// The line for the tenants without an owner: "2 huurders zonder eigenaar:
// één voor één".
export const withoutOwnerLabel = (count: number): string =>
  count === 1
    ? getLocalizedText("settlement.payments.without_owner_one")
    : getLocalizedText("settlement.payments.without_owner_many", {
        count: String(count),
      });
